import { Check, X, User, Star, Search, Building2, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { initPayment } from '../utils/payments'

interface PricingPageProps {
  onLoginClick: () => void
}

function CheckItem({ included, text }: { included: boolean; text: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      {included ? (
        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
      ) : (
        <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
      )}
      <span className={included ? 'text-slate-200' : 'text-slate-400 line-through'}>{text}</span>
    </li>
  )
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-white/5 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="text-white font-medium pr-4">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-slate-300 text-sm leading-relaxed">{answer}</p>}
    </div>
  )
}

export default function PricingPage({ onLoginClick }: PricingPageProps) {
  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="max-w-6xl mx-auto px-4 py-20 space-y-20">
        {/* Hero */}
        <section className="text-center space-y-6">
          <h1 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-white via-white to-green-500 bg-clip-text text-transparent">
            Choose the plan that's right for you
          </h1>
          <p className="text-lg text-slate-200 max-w-3xl mx-auto leading-relaxed">
            Whether you're a football player chasing your next opportunity, a scout searching for talent, or a club building the future, Scouta has a plan designed to help you succeed.
          </p>
        </section>

        {/* Players Section */}
        <section className="space-y-8">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-display font-bold">Players</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Player Free */}
            <div className="stagger-card animate-fade-in-up surface-panel rounded-3xl border border-white/10 p-8 space-y-6 flex flex-col">
              <div>
                <h3 className="text-xl font-bold text-white">Player Free</h3>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">₦0</span>
                  <span className="text-slate-400 text-sm ml-1">/ Month</span>
                </div>
                <p className="mt-3 text-slate-300 text-sm">Create your football profile and start getting discovered.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-green-400">Includes</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Basic player profile" />
                  <CheckItem included={true} text="1 highlight video" />
                  <CheckItem included={true} text="Up to 5 photos" />
                  <CheckItem included={true} text="Up to 3 achievements" />
                  <CheckItem included={true} text="1 document upload" />
                  <CheckItem included={true} text="Searchable on Scouta" />
                  <CheckItem included={true} text="1 active conversation" />
                  <CheckItem included={true} text="Basic profile visibility" />
                </ul>
                <p className="text-xs font-black uppercase tracking-widest text-slate-500 pt-2">Not Included</p>
                <ul className="space-y-2">
                  <CheckItem included={false} text="Trial Invitations" />
                  <CheckItem included={false} text="Unlimited messaging" />
                  <CheckItem included={false} text="Verified Player Badge" />
                  <CheckItem included={false} text="Profile Analytics" />
                  <CheckItem included={false} text="AI Profile Review" />
                  <CheckItem included={false} text="Agent details visibility" />
                  <CheckItem included={false} text="Featured Player eligibility" />
                </ul>
              </div>
              <button onClick={onLoginClick} className="w-full py-3 rounded-xl border border-white/10 bg-white/5 text-white font-semibold hover:bg-white/10 transition mt-auto">
                Create Free Account
              </button>
            </div>

            {/* Player Pro */}
            <div className="stagger-card animate-fade-in-up rounded-3xl border border-green-500/40 bg-green-500/5 p-8 space-y-6 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-black text-xs font-black uppercase tracking-widest px-4 py-1 rounded-full">
                Most Popular
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-xl font-bold text-white">Player Pro</h3>
                </div>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">₦4,000</span>
                  <span className="text-slate-400 text-sm ml-1">/ Month</span>
                </div>
                <p className="mt-3 text-slate-300 text-sm">Everything you need to maximise your visibility and increase your chances of getting discovered.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-green-400">Everything in Player Free, plus</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Unlimited highlight videos" />
                  <CheckItem included={true} text="Unlimited photos" />
                  <CheckItem included={true} text="Unlimited achievements" />
                  <CheckItem included={true} text="Unlimited document uploads" />
                  <CheckItem included={true} text="Unlimited messaging" />
                  <CheckItem included={true} text="Eligible to receive Trial Invitations" />
                  <CheckItem included={true} text="Verified Player Badge" />
                  <CheckItem included={true} text="Profile Analytics" />
                  <CheckItem included={true} text="AI Profile Review" />
                  <CheckItem included={true} text="Agent details visibility (where applicable)" />
                  <CheckItem included={true} text="Higher search visibility" />
                  <CheckItem included={true} text="Custom profile URL" />
                  <CheckItem included={true} text="Eligible for Featured Player promotion" />
                </ul>
              </div>
              <button
                onClick={async () => {
                  const { user } = useAuthStore.getState()
                  if (!user) return onLoginClick()
                  try {
                    const refCode = sessionStorage.getItem('scouta:referral_code') || undefined
                    const { authorization_url } = await initPayment('player_pro', refCode ? { referral_code: refCode } : undefined)
                    window.location.href = authorization_url
                  } catch (err: any) {
                    alert(err.message || 'Failed to start payment')
                  }
                }}
                className="w-full py-3 rounded-xl bg-green-500 text-black font-bold hover:bg-green-400 transition mt-auto"
              >
                Upgrade to Pro
              </button>
            </div>

            {/* Featured Player */}
            <div className="stagger-card animate-fade-in-up surface-panel rounded-3xl border border-yellow-500/30 bg-yellow-500/5 p-8 space-y-6 flex flex-col">
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-xl font-bold text-white">Featured Player</h3>
                </div>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">₦20,000</span>
                </div>
                <p className="mt-2 text-yellow-400 text-sm font-semibold">Featured for 7 Days</p>
                <p className="mt-2 text-slate-300 text-sm">Want maximum visibility? Become Scouta's Featured Player for an entire week and put your profile in front of clubs and scouts across the platform.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-yellow-400">Your profile will appear</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="At the top of the Top Talents page" />
                  <CheckItem included={true} text="On the Scouta homepage" />
                  <CheckItem included={true} text="Higher in player search results" />
                  <CheckItem included={true} text="Across Scouta's social media channels" />
                  <CheckItem included={true} text="In Scouta's weekly email to clubs and scouts" />
                </ul>
                <p className="text-xs font-black uppercase tracking-widest text-slate-500 pt-2">Eligibility</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Active Player Pro subscription" />
                  <CheckItem included={true} text="Verified account" />
                  <CheckItem included={true} text="Complete player profile" />
                </ul>
                <p className="text-xs text-slate-400 italic pt-2">
                  Note: Featured Player is a promotional placement and does not influence your ranking on the Top Talents leaderboard.
                </p>
              </div>
              <button
                onClick={async () => {
                  const { user } = useAuthStore.getState()
                  if (!user) return onLoginClick()
                  try {
                    const { players } = usePlayerStore.getState()
                    const currentPlayer = players.find(p =>
                      p.email?.toLowerCase() === user.email?.toLowerCase() ||
                      p.name?.toLowerCase() === user.name?.toLowerCase()
                    )
                    const refCode = sessionStorage.getItem('scouta:referral_code') || undefined
                    const meta: Record<string, any> = {}
                    if (currentPlayer?.id) meta.player_id = currentPlayer.id
                    if (refCode) meta.referral_code = refCode
                    const { authorization_url } = await initPayment('featured_player', Object.keys(meta).length ? meta : undefined)
                    window.location.href = authorization_url
                  } catch (err: any) {
                    alert(err.message || 'Failed to start payment')
                  }
                }}
                className="w-full py-3 rounded-xl border border-yellow-500/40 bg-yellow-500/10 text-yellow-400 font-semibold hover:bg-yellow-500/20 transition mt-auto"
              >
                Become Featured
              </button>
            </div>
          </div>
        </section>

        {/* Scouts Section */}
        <section className="space-y-8">
          <div className="flex items-center gap-3">
            <Search className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-display font-bold">Scouts</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {/* Scout Free Trial */}
            <div className="stagger-card animate-fade-in-up surface-panel rounded-3xl border border-white/10 p-8 space-y-6 flex flex-col">
              <div>
                <h3 className="text-xl font-bold text-white">Scout Free Trial</h3>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">Free</span>
                  <span className="text-slate-400 text-sm ml-1">for 7 Days</span>
                </div>
                <p className="mt-3 text-slate-300 text-sm">Experience Scouta's professional scouting tools before subscribing.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-green-400">Includes</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Browse player profiles" />
                  <CheckItem included={true} text="Search players" />
                  <CheckItem included={true} text="Basic player filters" />
                  <CheckItem included={true} text="Watch highlight videos" />
                  <CheckItem included={true} text="Save players to watchlists" />
                  <CheckItem included={true} text="Add scouting notes" />
                  <CheckItem included={true} text="Create up to 5 scouting reports" />
                  <CheckItem included={true} text="Send up to 10 player messages" />
                </ul>
              </div>
              <button onClick={onLoginClick} className="w-full py-3 rounded-xl border border-white/10 bg-white/5 text-white font-semibold hover:bg-white/10 transition mt-auto">
                Start Free Trial
              </button>
            </div>

            {/* Scout Pro */}
            <div className="stagger-card animate-fade-in-up rounded-3xl border border-green-500/40 bg-green-500/5 p-8 space-y-6 flex flex-col">
              <div>
                <h3 className="text-xl font-bold text-white">Scout Pro</h3>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">₦8,999</span>
                  <span className="text-slate-400 text-sm ml-1">/ Month</span>
                </div>
                <p className="mt-3 text-slate-300 text-sm">Built for scouts who need powerful discovery and recruitment tools.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-green-400">Includes</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Unlimited player search" />
                  <CheckItem included={true} text="Advanced search filters" />
                  <CheckItem included={true} text="Unlimited watchlists" />
                  <CheckItem included={true} text="Unlimited scouting reports" />
                  <CheckItem included={true} text="Unlimited player notes" />
                  <CheckItem included={true} text="Player comparison" />
                  <CheckItem included={true} text="Unlimited messaging" />
                  <CheckItem included={true} text="Recruitment tracking" />
                  <CheckItem included={true} text="Performance history" />
                  <CheckItem included={true} text="PDF & Excel report exports" />
                  <CheckItem included={true} text="Verified Scout Badge" />
                  <CheckItem included={true} text="Priority support" />
                </ul>
              </div>
              <button
                onClick={async () => {
                  const { user } = useAuthStore.getState()
                  if (!user) return onLoginClick()
                  try {
                    const refCode = sessionStorage.getItem('scouta:referral_code') || undefined
                    const { authorization_url } = await initPayment('scout_pro', refCode ? { referral_code: refCode } : undefined)
                    window.location.href = authorization_url
                  } catch (err: any) {
                    alert(err.message || 'Failed to start payment')
                  }
                }}
                className="w-full py-3 rounded-xl bg-green-500 text-black font-bold hover:bg-green-400 transition mt-auto"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        </section>

        {/* Clubs Section */}
        <section className="space-y-8">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-display font-bold">Clubs</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {/* Club Free */}
            <div className="stagger-card animate-fade-in-up surface-panel rounded-3xl border border-white/10 p-8 space-y-6 flex flex-col">
              <div>
                <h3 className="text-xl font-bold text-white">Club Free</h3>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">₦0</span>
                  <span className="text-slate-400 text-sm ml-1">/ Month</span>
                </div>
                <p className="mt-3 text-slate-300 text-sm">A great starting point for grassroots clubs and academies.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-green-400">Includes</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Public club profile" />
                  <CheckItem included={true} text="Add up to 5 players" />
                  <CheckItem included={true} text="1 active squad" />
                  <CheckItem included={true} text="Contact up to 3 players per month" />
                  <CheckItem included={true} text="Basic player search" />
                  <CheckItem included={true} text="Save up to 20 players" />
                  <CheckItem included={true} text="Basic dashboard" />
                </ul>
                <p className="text-xs font-black uppercase tracking-widest text-slate-500 pt-2">Not Included</p>
                <ul className="space-y-2">
                  <CheckItem included={false} text="Trial Invitations" />
                  <CheckItem included={false} text="Unlimited player management" />
                  <CheckItem included={false} text="Advanced analytics" />
                  <CheckItem included={false} text="Staff management" />
                  <CheckItem included={false} text="Match management" />
                  <CheckItem included={false} text="Live Match Recorder" />
                </ul>
              </div>
              <button onClick={onLoginClick} className="w-full py-3 rounded-xl border border-white/10 bg-white/5 text-white font-semibold hover:bg-white/10 transition mt-auto">
                Register Your Club
              </button>
            </div>

            {/* Club Pro */}
            <div className="stagger-card animate-fade-in-up rounded-3xl border border-green-500/40 bg-green-500/5 p-8 space-y-6 flex flex-col">
              <div>
                <h3 className="text-xl font-bold text-white">Club Pro</h3>
                <div className="mt-3">
                  <span className="text-3xl font-display font-bold">₦15,000</span>
                  <span className="text-slate-400 text-sm ml-1">/ Month</span>
                </div>
                <p className="mt-3 text-slate-300 text-sm">Everything your club needs to recruit, manage, and develop players.</p>
              </div>
              <div className="space-y-4 flex-1">
                <p className="text-xs font-black uppercase tracking-widest text-green-400">Everything in Club Free, plus</p>
                <ul className="space-y-2">
                  <CheckItem included={true} text="Unlimited players" />
                  <CheckItem included={true} text="Unlimited player messaging" />
                  <CheckItem included={true} text="Send Trial Invitations" />
                  <CheckItem included={true} text="Unlimited player search" />
                  <CheckItem included={true} text="Advanced filters" />
                  <CheckItem included={true} text="Unlimited watchlists" />
                  <CheckItem included={true} text="Unlimited staff accounts" />
                  <CheckItem included={true} text="Staff roles & permissions" />
                  <CheckItem included={true} text="Match management" />
                  <CheckItem included={true} text="Team analytics" />
                  <CheckItem included={true} text="Performance reports" />
                  <CheckItem included={true} text="PDF & Excel exports" />
                  <CheckItem included={true} text="Verified Club Badge" />
                  <CheckItem included={true} text="Priority support" />
                </ul>
              </div>
              <button
                onClick={async () => {
                  const { user } = useAuthStore.getState()
                  if (!user) return onLoginClick()
                  try {
                    const refCode = sessionStorage.getItem('scouta:referral_code') || undefined
                    const { authorization_url } = await initPayment('club_pro', refCode ? { referral_code: refCode } : undefined)
                    window.location.href = authorization_url
                  } catch (err: any) {
                    alert(err.message || 'Failed to start payment')
                  }
                }}
                className="w-full py-3 rounded-xl bg-green-500 text-black font-bold hover:bg-green-400 transition mt-auto"
              >
                Upgrade Club
              </button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-display font-bold">Frequently Asked Questions</h2>
            <div className="h-1 w-20 bg-green-500 mx-auto rounded-full" />
          </div>
          <div className="max-w-3xl mx-auto surface-panel rounded-3xl border border-white/10 px-8 divide-y divide-white/5">
            <FAQItem
              question="Can I switch plans anytime?"
              answer="Yes. You can upgrade or downgrade your subscription at any time."
            />
            <FAQItem
              question="What happens if my subscription expires?"
              answer="Your account will automatically revert to the Free plan. Premium features will be unavailable until you renew."
            />
            <FAQItem
              question="Who can purchase a Featured Player slot?"
              answer="Only players with an active Player Pro subscription, a verified account, and a complete profile are eligible."
            />
            <FAQItem
              question="Does Featured Player guarantee a trial or contract?"
              answer="No. Featured Player increases your visibility to clubs and scouts but does not guarantee trials, contracts, or selection."
            />
            <FAQItem
              question="Can clubs send Trial Invitations on the Free plan?"
              answer="No. Trial Invitations are available exclusively with Club Pro."
            />
            <FAQItem
              question="Do scouts have a free plan?"
              answer="Scouts receive a one-time 7-day free trial. After the trial, a Scout Pro subscription is required to continue using Scouta's scouting tools."
            />
          </div>
        </section>

        {/* CTA */}
        <section className="text-center space-y-8 py-12">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-4xl font-display font-bold">Ready to take the next step?</h2>
            <p className="text-lg text-slate-200 max-w-2xl mx-auto">
              Join thousands of players, scouts, and clubs using Scouta to discover talent, manage football careers, and build winning teams.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={onLoginClick} className="px-8 py-3 rounded-xl bg-green-500 text-black font-bold hover:bg-green-400 transition">
              Get Started Free
            </button>
            <button
              onClick={() => document.getElementById('pricing-plans')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-3 rounded-xl border border-white/10 bg-white/5 text-white font-semibold hover:bg-white/10 transition"
            >
              Compare Plans
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
